import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/imageUrl';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X, Image as ImageIcon } from 'lucide-react';
import ImageUploader from '../../components/admin/ImageUploader';

const empty = { imageUrl: '', title: '', category: '', description: '', order: 0 };

const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id...} = edit
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/gallery');
      setItems(data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.imageUrl) {
      toast.error("Veuillez ajouter une image (upload ou URL)");
      return;
    }
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing && editing.id) {
        await api.patch(`/admin/gallery/${editing.id}`, payload);
        toast.success('Image mise à jour');
      } else {
        await api.post('/admin/gallery', payload);
        toast.success('Image ajoutée');
      }
      close();
      load();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success('Image supprimée');
      load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div data-testid="admin-gallery-manager">
      <div className="flex justify-between items-center mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Portfolio</span>
          <h2 className="text-4xl font-serif text-[#111111]">Galerie</h2>
        </div>
        <button
          onClick={openNew}
          data-testid="gallery-add-btn"
          className="flex items-center gap-3 px-6 py-3 border border-[#111111] text-xs uppercase tracking-[0.3em] hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-all"
        >
          <Plus className="w-4 h-4" /> Ajouter une image
        </button>
      </div>

      {loading ? (
        <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#111111]/20">
          <ImageIcon className="w-10 h-10 text-[#6B6B6B] mx-auto mb-4" strokeWidth={1} />
          <p className="text-[#6B6B6B]">Aucune image. Cliquez sur "Ajouter".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((it) => (
            <div key={it.id} className="group relative border border-[#111111]/10 overflow-hidden bg-white" data-testid={`gallery-item-${it.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-[#F5F5F5]">
                <img src={resolveImageUrl(it.imageUrl)} alt={it.title || ''} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#111111]">{it.category || '—'}</span>
                <h3 className="font-serif text-xl text-[#111111] mt-1">{it.title || 'Sans titre'}</h3>
                <p className="text-xs text-[#6B6B6B] mt-1">Ordre: {it.order}</p>
              </div>
              <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(it)} data-testid={`gallery-edit-${it.id}`} className="w-9 h-9 bg-white/95 flex items-center justify-center border border-[#111111]/20 hover:border-[#111111]">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => del(it.id)} data-testid={`gallery-delete-${it.id}`} className="w-9 h-9 bg-white/95 flex items-center justify-center border border-[#111111]/20 hover:border-red-500 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={close}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-xl p-10 border-t-4 border-[#111111] relative"
            data-testid="gallery-form"
          >
            <button type="button" onClick={close} className="absolute top-5 right-5 text-[#6B6B6B] hover:text-[#111111]">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-serif mb-8 text-[#111111]">
              {editing.id ? "Modifier l'image" : 'Nouvelle image'}
            </h3>
            <div className="space-y-5">
              <Field label="Image" required>
                <ImageUploader
                  value={form.imageUrl}
                  onChange={(v) => setForm({ ...form, imageUrl: v })}
                  testId="gallery-form-imageurl"
                />
              </Field>
              <Field label="Titre">
                <input data-testid="gallery-form-title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className="luxury-input-light" />
              </Field>
              <Field label="Catégorie">
                <input data-testid="gallery-form-category" value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Design, Wedding, Planning…" className="luxury-input-light" />
              </Field>
              <Field label="Description">
                <textarea data-testid="gallery-form-description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="luxury-input-light" />
              </Field>
              <Field label="Ordre d'affichage">
                <input data-testid="gallery-form-order" type="number" value={form.order || 0} onChange={(e) => setForm({ ...form, order: e.target.value })} className="luxury-input-light" />
              </Field>
            </div>
            <div className="flex gap-4 mt-8">
              <button type="button" onClick={close} className="px-6 py-3 text-xs uppercase tracking-[0.3em] border border-[#111111]/30 hover:bg-[#F5F5F5]">Annuler</button>
              <button type="submit" data-testid="gallery-form-submit" className="flex-1 px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] transition-colors">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">
      {label}{required && <span className="text-[#111111]"> *</span>}
    </span>
    {children}
  </label>
);

export default GalleryManager;
